/**
 * Global operator chat alerts: in-page toast + chime + desktop notification.
 *
 * Fires IMMEDIATELY on an incoming (customer/driver) message — not on a delay. A dispatcher sitting at
 * the desk should know at once; a delay is for *escalation* (nobody picked it up), which is a separate
 * mechanism. The desktop notification is what reaches a dispatcher whose tab is backgrounded — the one
 * case an in-page toast can never cover.
 *
 * Deliberately dependency-free (own toast DOM, WebAudio chime) so it cannot clash with the SweetAlert2
 * modals used elsewhere and needs no audio asset.
 */

const TOAST_TTL_MS = 8000;
const ALERT_DEDUPE_TTL_MS = 30000;

export interface IncomingChatAlert {
  id?: string;
  conversationId?: string;
  from?: string; // user | agent | driver | system
  text?: string;
  type?: string; // text | image | location
}

// ── de-dupe ──────────────────────────────────────────────────────────────────
// The shared SignalR connection is joined to BOTH the conversation group and the website group, so the
// same message can arrive twice. Alert once per message id.
const recentlyAlerted = new Set<string>();
function alreadyAlerted(id?: string): boolean {
  if (!id) return false;
  if (recentlyAlerted.has(id)) return true;
  recentlyAlerted.add(id);
  window.setTimeout(() => recentlyAlerted.delete(id), ALERT_DEDUPE_TTL_MS);
  return false;
}

// ── sound ────────────────────────────────────────────────────────────────────
let audioCtx: AudioContext | null = null;

/** Browsers block audio until a user gesture — resume the context on the operator's first interaction. */
export function initChatSound(): void {
  const unlock = () => {
    try {
      const Ctx: typeof AudioContext | undefined =
        window.AudioContext || (window as any).webkitAudioContext;
      if (!Ctx) return;
      if (!audioCtx) audioCtx = new Ctx();
      if (audioCtx.state === 'suspended') void audioCtx.resume();
    } catch {
      /* audio unavailable — visual alerts still work */
    }
    window.removeEventListener('click', unlock);
    window.removeEventListener('keydown', unlock);
  };
  window.addEventListener('click', unlock);
  window.addEventListener('keydown', unlock);
}

/**
 * Short three-tone chime (no audio asset needed). No-ops until the context is unlocked.
 *
 * Volume and shape are deliberate, after dispatchers reported the original was too quiet to hear over a
 * busy room: a `sine` at 0.15 peak is barely present on laptop speakers. Three things fix that together —
 * a higher peak, a longer tail so the ear has time to register it, and a `triangle` wave, whose harmonics
 * cut through background noise far better than a pure sine at the same nominal gain.
 */
function playChime(): void {
  const ctx = audioCtx;
  if (!ctx || ctx.state !== 'running') return;
  const now = ctx.currentTime;
  [{ f: 880, t: 0 }, { f: 1174, t: 0.14 }, { f: 1568, t: 0.28 }].forEach(({ f, t }) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'triangle';
    osc.frequency.value = f;
    gain.gain.setValueAtTime(0.0001, now + t);
    gain.gain.exponentialRampToValueAtTime(0.5, now + t + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + t + 0.2);
    osc.connect(gain).connect(ctx.destination);
    osc.start(now + t);
    osc.stop(now + t + 0.22);
  });
}

// ── desktop notifications ────────────────────────────────────────────────────
/** Ask once. Only call this for operators who actually have chat enabled. */
export function initChatDesktopNotifications(): void {
  try {
    if ('Notification' in window && Notification.permission === 'default') {
      void Notification.requestPermission();
    }
  } catch {
    /* unsupported — toast + chime still work */
  }
}

function showDesktopNotification(title: string, body: string, conversationId: string): void {
  try {
    if (!('Notification' in window) || Notification.permission !== 'granted') return;
    // `tag` collapses repeats for the same conversation instead of stacking a wall of popups.
    const n = new Notification(title, { body, tag: `cbs-chat-${conversationId}` });
    n.onclick = () => {
      window.focus();
      openConversation(conversationId);
      n.close();
    };
  } catch {
    /* ignore */
  }
}

// ── navigation ───────────────────────────────────────────────────────────────
function openConversation(conversationId: string): void {
  window.location.hash = `/support/conversations/${conversationId}`;
}

function isViewingConversation(conversationId: string): boolean {
  return (window.location.hash || '').includes(`/support/conversations/${conversationId}`);
}

// ── toast ────────────────────────────────────────────────────────────────────
function toastContainer(): HTMLElement {
  let el = document.getElementById('cbs-chat-toasts');
  if (!el) {
    el = document.createElement('div');
    el.id = 'cbs-chat-toasts';
    el.style.cssText =
      'position:fixed;top:16px;left:50%;transform:translateX(-50%);z-index:20000;' +
      'display:flex;flex-direction:column;gap:8px;align-items:center;pointer-events:none;';
    document.body.appendChild(el);
  }
  return el;
}

function showToast(title: string, body: string, onOpen: () => void): void {
  const container = toastContainer();

  const toast = document.createElement('div');
  toast.style.cssText =
    'pointer-events:auto;min-width:300px;max-width:440px;background:#fff;color:#212529;' +
    'border:1px solid #e3e6ea;border-left:4px solid #e67e22;border-radius:6px;' +
    'box-shadow:0 6px 20px rgba(0,0,0,.16);padding:10px 12px;cursor:pointer;' +
    'display:flex;gap:10px;align-items:flex-start;font-size:13px;line-height:1.35;';

  const textWrap = document.createElement('div');
  textWrap.style.cssText = 'flex:1;min-width:0;';

  const titleEl = document.createElement('div');
  titleEl.style.cssText = 'font-weight:600;margin-bottom:2px;';
  titleEl.textContent = title;

  const bodyEl = document.createElement('div');
  bodyEl.style.cssText =
    'color:#6c757d;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;';
  bodyEl.textContent = body;

  textWrap.appendChild(titleEl);
  textWrap.appendChild(bodyEl);

  const close = document.createElement('button');
  close.type = 'button';
  close.setAttribute('aria-label', 'Dismiss');
  close.textContent = '✕';
  close.style.cssText =
    'background:none;border:none;color:#adb5bd;cursor:pointer;padding:0 2px;font-size:12px;line-height:1;';

  const dismiss = () => {
    window.clearTimeout(timer);
    toast.remove();
  };

  close.addEventListener('click', (e) => {
    e.stopPropagation();
    dismiss();
  });
  toast.addEventListener('click', () => {
    dismiss();
    onOpen();
  });

  toast.appendChild(textWrap);
  toast.appendChild(close);
  container.appendChild(toast);

  const timer = window.setTimeout(dismiss, TOAST_TTL_MS);
}

// ── entry point ──────────────────────────────────────────────────────────────
function snippetFor(msg: IncomingChatAlert): string {
  if (msg.type === 'image') return '[image]';
  if (msg.type === 'location') return '[location]';
  const text = (msg.text || '').trim();
  if (!text) return 'New message';
  return text.length > 90 ? `${text.slice(0, 88)}…` : text;
}

/**
 * Alert the operator about an incoming chat message. Safe to call for every SignalR message —
 * it filters to inbound (customer/driver) messages and de-dupes.
 */
export function notifyIncomingChat(msg: IncomingChatAlert): void {
  if (!msg) return;
  // Only INBOUND messages need a nudge — never our own or another agent's.
  if (msg.from !== 'user' && msg.from !== 'driver') return;

  const conversationId = msg.conversationId;
  if (!conversationId) return;
  if (alreadyAlerted(msg.id)) return;

  // Can the operator actually see the page right now? (Hidden tab OR another app in front.)
  const pageVisible = !document.hidden && document.hasFocus();

  // Already reading this exact thread → they can see the message land. Don't nag.
  if (pageVisible && isViewingConversation(conversationId)) return;

  const who = msg.from === 'driver' ? 'driver' : 'customer';
  const title = `New ${who} message`;
  const body = snippetFor(msg);

  playChime();
  showToast(title, body, () => openConversation(conversationId));

  // The only channel that reaches a backgrounded tab / unfocused window.
  if (!pageVisible) showDesktopNotification(title, body, conversationId);
}
